import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import NegotiationBadge from '@/components/NegotiationBadge';
import { FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuotationsDialog = ({ req, onClose, onSaved }) => {
  const { getAuthHeader } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [formData, setFormData] = useState({
    vendor_name: req.vendor_name || '',
    price: '',
    delivery_timeline: '',
    payment_terms: ''
  });

  const loadQuotations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/quotations?requisition_id=${req.id}`, { headers: getAuthHeader() });
      setQuotations(response.data);
    } catch (error) {
      toast.error('Failed to load quotations');
    }
  }, [req.id, getAuthHeader]);

  useEffect(() => {
    loadQuotations();
  }, [loadQuotations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/quotations`, {
        ...formData,
        requisition_id: req.id,
        price: parseFloat(formData.price)
      }, { headers: getAuthHeader() });
      toast.success('Quotation added successfully');
      setFormData({ vendor_name: '', price: '', delivery_timeline: '', payment_terms: '' });
      loadQuotations();
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add quotation');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Quotations for {req.reagent_name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 p-4 bg-surface rounded">
            <div>
              <p className="text-sm text-text-tertiary">Catalogue Number</p>
              <p className="font-mono text-text-primary">{req.catalogue_number}</p>
            </div>
            <div>
              <p className="text-sm text-text-tertiary">Project</p>
              <p className="text-text-primary">{req.project_code_name}</p>
            </div>
          </div>

          {quotations.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold text-text-primary">Existing Quotations ({quotations.length})</h3>
              {quotations.map((quot) => (
                <div key={quot.id} className="border border-border-default p-4 rounded" data-testid={`quotation-${quot.id}`}>
                  <div className="mb-2">
                    <NegotiationBadge quot={quot} />
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-text-tertiary">Vendor</p>
                      <p className="font-medium text-text-primary">{quot.vendor_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">Price</p>
                      <p className="font-semibold text-accent-brand">
                        ₹{quot.price.toFixed(2)}
                        {quot.negotiation_status === 'negotiated' && quot.original_price != null && quot.original_price !== quot.price && (
                          <span className="ml-2 text-sm text-text-tertiary line-through font-normal">₹{quot.original_price.toFixed(2)}</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">Delivery</p>
                      <p className="text-text-primary">{quot.delivery_timeline}</p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">Payment Terms</p>
                      <p className="text-text-primary">{quot.payment_terms}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border-t pt-4">
            <h3 className="font-semibold text-text-primary mb-2">Add New Quotation</h3>
            <div className="mb-4 flex items-start gap-2 rounded border border-accent-brand/30 bg-accent-brand-subtle/40 p-3 text-xs text-text-secondary" data-testid="pdf-upload-nudge">
              <FilePdf size={18} className="mt-0.5 shrink-0 text-accent-brand" weight="duotone" />
              <div>
                <span className="font-semibold text-text-primary">Tip:</span> If you have the vendor&apos;s quotation
                as a PDF, close this dialog and click <span className="font-semibold">&ldquo;Import Quotation PDF&rdquo;</span>{' '}
                at the top of the page — Finance will then receive the original vendor document instead of an
                auto-generated summary.
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vendor_name">Vendor Name</Label>
                  <Input
                    id="vendor_name"
                    value={formData.vendor_name}
                    onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                    required
                    data-testid="quotation-vendor-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Price (₹)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    data-testid="quotation-price-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="delivery_timeline">Delivery Timeline</Label>
                  <Input
                    id="delivery_timeline"
                    value={formData.delivery_timeline}
                    onChange={(e) => setFormData({ ...formData, delivery_timeline: e.target.value })}
                    placeholder="e.g., 7-10 days"
                    required
                    data-testid="quotation-delivery-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payment_terms">Payment Terms</Label>
                  <Input
                    id="payment_terms"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    placeholder="e.g., Net 30"
                    required
                    data-testid="quotation-payment-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full bg-accent-brand hover:bg-accent-brand-hover text-white" data-testid="submit-quotation-button">
                Add Quotation
              </Button>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuotationsDialog;
